import type { CaptureDetailRow, NamedCountRow, NormalizedDetailRow, StaffRow } from './types';

export const num = (value: unknown): string => {
  if (value == null || value === '') return '-';
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('vi-VN') : String(value);
};

export const asNumber = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export const pct = (value: unknown): string => {
  if (value == null || value === '') return '-';
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)}%` : '-';
};

export const money = (value: unknown): string => {
  const n = asNumber(value);
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)} tỷ`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)} tr`;
  return num(n);
};

export const uniq = <T,>(items: Array<T | null | undefined | ''>): T[] =>
  [...new Set(items.filter(Boolean) as T[])].sort((a, b) => String(a).localeCompare(String(b), 'vi'));

export const optionLabel = (value: string): string => ({
  has_phone: 'Có SĐT',
  no_phone: 'Thiếu SĐT',
  has_chat: 'Có hội thoại',
  no_chat: 'Chưa có hội thoại',
  contactable: 'Có liên hệ',
  missing_contact: 'Thiếu liên hệ',
  phone: 'Có SĐT',
  ordered: 'Có mã đơn',
  awaiting_shop: 'Chờ cửa hàng',
  in_conversation: 'Đang trao đổi',
  new_unanswered: 'Mới/chưa trả lời',
  lead: 'Khớp khách tiềm năng',
  order: 'Có mã đơn',
  buyer: 'Đã mua hàng',
  passed: 'Đủ dữ liệu',
  needs_review: 'Cần kiểm tra',
  rejected: 'Loại bỏ',
}[value] || value);

export const needTypeLabel = (value?: string | null): string =>
  value === 'service' ? 'Dịch vụ' : value === 'missing_info' ? 'Thiếu thông tin' : 'Mua hàng';

export const statusLabel = (value?: string | null): string => ({
  closed: 'Đã mua',
  ordered: 'Có mã đơn/PYC',
  hopeful: 'Đang tư vấn / hẹn quay lại',
  not_closed: 'Chưa chốt',
  service: 'Dịch vụ / bảo hành',
  warranty: 'Dịch vụ / bảo hành',
  in_conversation: 'Đang trao đổi',
  new: 'Mới / chưa rõ',
  other: 'Khác',
}[value || ''] || 'Mới / chưa rõ');

export const qcLabel = (value?: string | null): string => ({
  passed: 'Đủ dữ liệu chăm sóc',
  missing_contact: 'Thiếu liên hệ',
  needs_review: 'Cần kiểm tra',
  rejected: 'Loại bỏ',
}[value || ''] || 'Cần kiểm tra');

export const colorStatus = (label: string): 'red' | 'green' | 'blue' | 'amber' | 'gray' => {
  if (label.includes('Đã mua') || label.includes('mã đơn')) return 'green';
  if (label.includes('Chưa')) return 'red';
  if (label.includes('Dịch vụ')) return 'blue';
  if (label.includes('hẹn') || label.includes('Đang')) return 'amber';
  return 'gray';
};

export const colorQc = (status?: string | null): 'red' | 'green' | 'amber' =>
  status === 'passed' ? 'green' : status === 'missing_contact' ? 'amber' : 'red';

export const colorNeed = (label: string): 'green' | 'blue' | 'amber' =>
  label === 'Dịch vụ' ? 'blue' : label === 'Thiếu thông tin' ? 'amber' : 'green';

export const colorKpi = (status?: string | null): 'red' | 'green' | 'amber' | 'gray' =>
  status === 'met' ? 'green' : status === 'at_risk' ? 'amber' : status === 'breach' ? 'red' : 'gray';

export const kpiLabel = (status?: string | null): string =>
  status === 'met' ? 'Đạt' : status === 'at_risk' ? 'Cảnh báo' : status === 'breach' ? 'Chưa đạt' : 'Chưa có dữ liệu';

export function normalizeMissingFields(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (!value) return [];
  if (typeof value === 'string') {
    return value.replace(/[{}"]/g, '').split(',').map(item => item.trim()).filter(Boolean);
  }
  return [];
}

export function normalizeDetailRow(row: CaptureDetailRow, index: number): NormalizedDetailRow {
  const day = row.reported_date || '';
  const phone = row.phone || '';
  const needType = row.need_type || 'missing_info';
  const status = row.status || 'new';
  const qcStatus = row.qc_status || (phone ? 'passed' : 'missing_contact');
  return {
    capture_id: row.capture_id || `LIVE-${index + 1}`,
    source_label: row.source_system || 'DB',
    store_code: row.store_code || 'Chưa rõ',
    staff_name: row.staff || 'Chưa rõ nhân viên',
    customer: row.customer_name || 'Chưa rõ',
    phone_masked: phone || null,
    has_contact: Boolean(phone),
    need_text: row.need_text || 'Chưa rõ nhu cầu',
    need_type: needType,
    need_type_label: needTypeLabel(needType),
    industry: row.industry || 'Chưa rõ',
    brand: row.brand || 'Chưa rõ',
    product: row.product || 'Chưa rõ',
    store_care_status: status,
    store_care_status_label: statusLabel(status),
    agent_qc_status: qcStatus,
    agent_qc_status_label: qcLabel(qcStatus),
    missing_fields: normalizeMissingFields(row.missing_fields),
    day,
    month: day ? day.slice(0, 7) : '',
  };
}

export function sortNewestFirst<T extends { day?: string; capture_id?: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ta = Date.parse(a.day || '') || 0;
    const tb = Date.parse(b.day || '') || 0;
    return tb - ta || String(b.capture_id || '').localeCompare(String(a.capture_id || ''));
  });
}

export function exportCsv(rows: unknown[], _summary?: { store?: NamedCountRow[] | unknown[]; staff?: StaffRow[] | unknown[] }, name = 'Care_CDP_QC'): void {
  const objects = rows.map(row => row && typeof row === 'object' ? row as Record<string, unknown> : { value: row });
  const headers = [...new Set(objects.flatMap(row => Object.keys(row)))];
  const escape = (value: unknown) => {
    if (value == null) return '';
    const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const csv = [headers.join(','), ...objects.map(row => headers.map(header => escape(row[header])).join(','))].join('\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ═══════════════ Ops Quality helpers (Chất lượng vận hành) ═══════════════

export const monthLabel = (ym?: string | null): string => {
  if (!ym || ym.length < 7) return '-';
  return `T${Number(ym.slice(5, 7))}/${ym.slice(0, 4)}`;
};

export const pct1 = (value: unknown): string => {
  if (value == null || value === '') return '-';
  const n = Number(value);
  return Number.isFinite(n) ? `${Math.round(n * 10) / 10}%` : '-';
};

// Trạng thái scorecard ops-quality: vuot | dat | chua_dat.
export const opsStatusLabel = (status?: string | null): string =>
  status === 'vuot' ? 'Vượt' : status === 'dat' ? 'Đạt' : status === 'chua_dat' ? 'Chưa đạt' : '-';

export const opsStatusTone = (status?: string | null): 'green' | 'blue' | 'red' | 'gray' =>
  status === 'vuot' ? 'green' : status === 'dat' ? 'blue' : status === 'chua_dat' ? 'red' : 'gray';

// Trạng thái phiếu phạt khiếu nại: pending | confirmed | waived.
export const penaltyStatusLabel = (status?: string | null): string =>
  status === 'confirmed' ? 'Phạt' : status === 'waived' ? 'Gỡ phạt' : status === 'pending' ? 'Chờ xử lý' : status || '-';

export const penaltyStatusTone = (status?: string | null): 'red' | 'green' | 'amber' | 'gray' =>
  status === 'confirmed' ? 'red' : status === 'waived' ? 'green' : status === 'pending' ? 'amber' : 'gray';

// Trạng thái duyệt giải trình: pending | approved | rejected.
export const reviewStatusLabel = (status?: string | null): string =>
  status === 'approved' ? 'Duyệt' : status === 'rejected' ? 'Từ chối' : status === 'pending' ? 'Chờ duyệt' : 'Chưa giải trình';

export const reviewStatusTone = (status?: string | null): 'green' | 'red' | 'amber' | 'gray' =>
  status === 'approved' ? 'green' : status === 'rejected' ? 'red' : status === 'pending' ? 'amber' : 'gray';

// Nguồn phiếu phạt khiếu nại.
export const penaltySourceLabel = (source?: string | null): string =>
  source === 'google_review' ? 'Google' : source === 'zalo_review' ? 'Zalo' : source === 'chat_complaint' ? 'Chat' : source || '-';

// Màu series cố định theo store (đọc CSS token lúc render qua CHART_COLORS).
export const STORE_SERIES_COLORS: Record<string, string> = {
  NCV: '#2563eb', HHT: '#d97706', KVC: '#059669', TBT: '#7c3aed', TH: '#0891b2', ONL: '#d71920', TMDT: '#6b7280',
};
export const storeSeriesColor = (code: string): string => STORE_SERIES_COLORS[code] || '#6b7280';

// Đếm ngược SLA: remaining_h (giờ, âm = quá hạn) → chuỗi đọc được.
export const fmtRemain = (h?: number | null): string => {
  if (h == null || !Number.isFinite(Number(h))) return '-';
  const n = Number(h);
  const abs = Math.abs(n);
  const label = abs >= 48
    ? `${Math.floor(abs / 24)} ngày ${Math.round(abs % 24)}g`
    : abs >= 1
      ? `${Math.floor(abs)}g ${Math.round((abs % 1) * 60)}p`
      : `${Math.round(abs * 60)}p`;
  return n < 0 ? `quá hạn ${label}` : `còn ${label}`;
};

export const remainTone = (h?: number | null): 'red' | 'amber' | 'green' | 'gray' => {
  if (h == null || !Number.isFinite(Number(h))) return 'gray';
  const n = Number(h);
  if (n < 0) return 'red';
  if (n < 4) return 'red';
  if (n < 12) return 'amber';
  return 'green';
};

export const complaintLevelLabel = (level?: string | null): string =>
  level === 'high' ? 'Cao' : level === 'medium' ? 'Trung bình' : level === 'low' ? 'Thấp' : level || '-';

export const complaintLevelTone = (level?: string | null): 'red' | 'amber' | 'gray' =>
  level === 'high' ? 'red' : level === 'medium' ? 'amber' : 'gray';
